import React from "react";
import { View, ScrollView, TouchableOpacity } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { cn } from "@/lib/utils";

export interface TabBarItem<T extends string = string> {
  key: T;
  label: string;
  count?: number;
}

export interface TabBarNavigationProps<T extends string = string> {
  /** Array of tabs to render. */
  tabs: TabBarItem<T>[];
  /** Currently active tab. */
  activeTab?: T;
  /** Called when the user presses a tab. */
  onTabPress?: (tab: T) => void;
  testID?: string;
  className?: string;
  contentContainerClassName?: string;
  containerClassName?: string;
}

/**
 * Horizontal tab bar for the staff web portal.
 *
 * Mirrors the "Top Navigation" component from the Figma design (node 12182:10960).
 * Contains four tabs: Overview, Invitations, Applications, Admin Users.
 *
 * - Active tab: `bg-primary-5` (Main Color/05 = #FFE8EE) with `text-primary` label.
 * - Inactive tab: `text-supporting-text` label, transparent bg.
 * - Dividers rendered above and below the tab row via `h-px bg-stroke-50`.
 * - Horizontally scrollable so it adapts to smaller viewports.
 *
 * @example
 * <TabBarNavigation activeTab="applications" onTabPress={setActiveTab} />
 */
// Use a standard function to support generics
export function TabBarNavigation<T extends string = string>({
  tabs,
  activeTab,
  onTabPress,
  testID,
  className,
  contentContainerClassName,
  containerClassName,
}: TabBarNavigationProps<T>) {
  return (
    <View className="bg-white" testID={testID}>
      {/* Top divider */}
      <View className="h-px bg-stroke-50" />

      {/* Tab row */}
      <View className={containerClassName}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerClassName={cn("gap-2 items-center", contentContainerClassName)}
          className={cn("h-[54px]", className)}
        >
          {tabs.map((tab) => {
            const isActive = tab.key === activeTab;
            return (
              <TouchableOpacity
                key={tab.key}
                onPress={() => onTabPress?.(tab.key)}
                accessibilityRole="tab"
                accessibilityLabel={tab.label}
                accessibilityState={{ selected: isActive }}
                className={`px-4 py-2 rounded-sm items-center justify-center flex-row gap-2 ${
                  isActive ? "bg-primary-5" : ""
                }`}
              >
                <AppText
                  weight="medium"
                  className={`text-base leading-[1.4] ${
                    isActive ? "text-primary" : "text-supporting-text"
                  }`}
                >
                  {tab.label}
                </AppText>
                {tab.count !== undefined && tab.count > 0 && (
                  <View
                    className={`px-1.5 py-0.5 min-w-5 rounded-full items-center justify-center ${isActive ? "bg-white" : "bg-stroke-10"}`}
                  >
                    <AppText
                      weight="medium"
                      className={`text-xs text-center ${isActive ? "text-primary" : "text-dark-text-15"}`}
                    >
                      {tab.count}
                    </AppText>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      {/* Bottom divider */}
      <View className="h-px bg-stroke-50" />
    </View>
  );
}
